import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type SetStateAction } from "react";
import { useUserSettingsStore } from "./user-settings.store";

const PREFIX = "chessla:page-state:v1:";
const MAX_BYTES = 16_384;
let revision = 0;
const listeners = new Set<() => void>();
const resetListeners = new Set<(owner: string) => void>();
export const subscribePageStateReset = (listener: (owner: string) => void) => {
  resetListeners.add(listener);
  return () => {
    resetListeners.delete(listener);
  };
};
const memory = new Map<string, string>();
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
const notify = () => {
  revision++;
  listeners.forEach((listener) => listener());
};
const keyFor = (page: string) => {
  const { ownerUserId, settings } = useUserSettingsStore.getState();
  return ownerUserId && settings.rememberPageState && /^[\w:/+. -]{1,180}$/.test(page)
    ? `${PREFIX}${encodeURIComponent(ownerUserId)}:${page}`
    : null;
};
const valid = (value: unknown, depth = 0): boolean => {
  if (depth > 4) return false;
  if (value === null || typeof value === "boolean") return true;
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value === "string") return value.length <= 1024;
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  return (
    Object.entries(value).length <= 24 &&
    Object.entries(value).every(
      ([key, item]) =>
        !["__proto__", "constructor", "prototype", "pgn", "analysis"].includes(key) && valid(item, depth + 1),
    )
  );
};
const matchesInitial = (value: unknown, initial: unknown): boolean => {
  if (initial === null) return true;
  if (typeof value !== typeof initial || value === null) return false;
  if (typeof initial !== "object") return true;
  return Object.entries(initial).every(([key, item]) => matchesInitial((value as Record<string, unknown>)[key], item));
};

export const readPageState = <T>(page: string, validate?: (value: unknown) => value is T): T | undefined => {
  const key = keyFor(page);
  if (!key) return undefined;
  let raw = memory.get(key);
  try {
    raw = window.localStorage.getItem(key) ?? raw;
  } catch {
    /* Browser storage may be disabled. */
  }
  if (!raw || raw.length > MAX_BYTES) return undefined;
  try {
    const value: unknown = JSON.parse(raw);
    return valid(value) && (!validate || validate(value)) ? (value as T) : undefined;
  } catch {
    return undefined;
  }
};

export const writePageState = (page: string, value: unknown): void => {
  const key = keyFor(page);
  if (!key || !valid(value)) return;
  const raw = JSON.stringify(value);
  if (raw.length > MAX_BYTES) return;
  memory.set(key, raw);
  if (memory.size > 128) memory.delete(memory.keys().next().value!);
  try {
    window.localStorage.setItem(key, raw);
    const prefix = `${PREFIX}${encodeURIComponent(useUserSettingsStore.getState().ownerUserId!)}:`;
    const keys = Object.keys(window.localStorage).filter((item) => item.startsWith(prefix) && item !== key);
    for (const old of keys.slice(0, Math.max(0, keys.length - 127))) window.localStorage.removeItem(old);
  } catch {
    /* Keep this session usable without storage. */
  }
};

export const resetPageState = (): void => {
  const owner = useUserSettingsStore.getState().ownerUserId;
  if (!owner) return;
  const prefix = `${PREFIX}${encodeURIComponent(owner)}:`;
  for (const key of memory.keys()) if (key.startsWith(prefix)) memory.delete(key);
  try {
    for (const key of Object.keys(window.localStorage)) if (key.startsWith(prefix)) window.localStorage.removeItem(key);
  } catch {
    /* Memory reset still applies. */
  }
  resetListeners.forEach((listener) => listener(owner));
  notify();
};

export const useRememberedPageState = <T>(page: string, initial: T, validate?: (value: unknown) => value is T) => {
  const initialRef = useRef(initial);
  initialRef.current = initial;
  const validateRef = useRef(validate);
  validateRef.current = validate;
  const owner = useUserSettingsStore((state) => state.ownerUserId);
  const enabled = useUserSettingsStore((state) => state.settings.rememberPageState);
  const loading = useUserSettingsStore((state) => state.loading);
  const version = useSyncExternalStore(
    subscribe,
    () => revision,
    () => revision,
  );
  const scope = `${owner}:${enabled}:${loading}:${version}:${page}`;
  const read = useCallback(
    () =>
      readPageState<T>(page, validateRef.current ?? ((value): value is T => matchesInitial(value, initialRef.current))),
    [page],
  );
  const [entry, setEntry] = useState(() => ({ scope, value: read() ?? initial }));
  const value = entry.scope === scope ? entry.value : (read() ?? initial);
  const setValue = useCallback(
    (next: SetStateAction<T>) => {
      setEntry((current) => {
        const previous = current.scope === scope ? current.value : (read() ?? initialRef.current);
        const value = typeof next === "function" ? (next as (previous: T) => T)(previous) : next;
        if (owner === useUserSettingsStore.getState().ownerUserId) writePageState(page, value);
        return { scope, value };
      });
    },
    [scope, page, owner, read],
  );
  return [value, setValue] as const;
};

export const rememberRoute = (section: string, route: string): void => {
  if (route === section || route.startsWith(`${section}/`) || route.startsWith(`${section}?`)) {
    writePageState(`route:${section}`, route);
    listeners.forEach((listener) => listener());
  }
};
export const getRememberedRoute = (section: string, fallback = section): string => {
  const route = readPageState<unknown>(`route:${section}`);
  return typeof route === "string" &&
    (route === section || route.startsWith(`${section}/`) || route.startsWith(`${section}?`))
    ? route
    : fallback;
};

export const useRememberedRoute = (section: string) => {
  useUserSettingsStore((state) => state.ownerUserId);
  useUserSettingsStore((state) => state.settings.rememberPageState);
  return useSyncExternalStore(
    subscribe,
    () => getRememberedRoute(section),
    () => section,
  );
};

export const useRememberedScroll = (page: string, element: HTMLElement | null, ready = true) => {
  const owner = useUserSettingsStore((state) => state.ownerUserId);
  const enabled = useUserSettingsStore((state) => state.settings.rememberPageState);
  useEffect(() => {
    if (!element || !ready) return;
    const key = `scroll:${page}`;
    const saved = readPageState<unknown>(key);
    let restored = typeof saved !== "number" || saved < 0 || saved > 10_000_000;
    const restore = () => {
      if (restored) return;
      element.scrollTop = saved as number;
      restored = element.scrollTop >= (saved as number) - 1;
    };
    restore();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(restore);
    observer?.observe(element);
    for (const child of element.children) observer?.observe(child);
    const save = () => {
      if (restored && element.style.touchAction !== "none" && owner === useUserSettingsStore.getState().ownerUserId)
        writePageState(key, element.scrollTop);
    };
    const interrupt = () => {
      restored = true;
    };
    element.addEventListener("scroll", save, { passive: true });
    element.addEventListener("wheel", interrupt, { passive: true });
    element.addEventListener("touchstart", interrupt, { passive: true });
    return () => {
      observer?.disconnect();
      element.removeEventListener("scroll", save);
      element.removeEventListener("wheel", interrupt);
      element.removeEventListener("touchstart", interrupt);
    };
  }, [page, element, ready, owner, enabled]);
};
